import { useSyncExternalStore } from 'react';

import type { components } from './schema';

/**
 * Sesión en memoria: el access token nunca va a localStorage (ante XSS no queda persistido). El
 * refresh vive en una cookie httpOnly SameSite=Strict que emite el API en modo cookie; la web no
 * la ve. Al recargar la página, la sesión se recupera con un refresh (ver `client.ts`).
 */

/** Respuesta de verify-otp y de `POST /api/v1/auth/refresh` (en modo cookie, `refresh_token` es null). */
export type TokenResponse = components['schemas']['TokenResponse'];

/** Usuario de la sesión (id, rol, email, teléfono, nombre visible). */
export type SessionUser = components['schemas']['TokenUser'];

export interface Session {
  accessToken: string;
  tokenType: string;
  /** Epoch en ms en que vence el access token. */
  expiresAt: number;
  user: SessionUser;
}

type Listener = () => void;
type ExpiredListener = (user: SessionUser) => void;

export class SessionStore {
  private session: Session | null = null;
  private readonly listeners = new Set<Listener>();
  private readonly expiredListeners = new Set<ExpiredListener>();
  private rejected = false;

  get = (): Session | null => this.session;

  getAccessToken = (): string | null => this.session?.accessToken ?? null;

  set = (session: Session | null): void => {
    this.session = session;
    if (session) this.rejected = false;
    for (const listener of this.listeners) listener();
  };

  setFromTokenResponse = (response: TokenResponse, now: number = Date.now()): Session => {
    const session: Session = {
      accessToken: response.access_token,
      tokenType: response.token_type,
      expiresAt: now + response.expires_in * 1000,
      user: response.user,
    };
    this.set(session);
    return session;
  };

  clear = (): void => {
    if (this.session !== null) this.set(null);
  };

  /**
   * La sesión se perdió porque el API la rechazó (caducó o se revocó), no porque la persona cerrara
   * sesión (eso es `clear`). Avisa solo si había una sesión con token: la sesión sin conexión no cuenta.
   */
  expire = (): void => {
    const lost = this.session;
    this.rejected = lost !== null;
    this.clear();
    if (!lost?.accessToken) return;
    for (const listener of [...this.expiredListeners]) listener(lost.user);
  };

  /**
   * La última sesión se perdió porque el API la rechazó (`expire`), no por cerrar sesión ni por falta
   * de red. Quien lo consulta sabe que el API respondió: no hace falta probar si está disponible.
   */
  wasRejected = (): boolean => this.rejected;

  subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  /** Se llama cada vez que `expire` pierde una sesión con token. */
  onExpired = (listener: ExpiredListener): (() => void) => {
    this.expiredListeners.add(listener);
    return () => this.expiredListeners.delete(listener);
  };
}

/** Sesión única de la app. */
export const sessionStore = new SessionStore();

/**
 * Hay una sesión de cliente. Las de estación y administración existen en el mismo navegador pero no
 * son un cliente: las pantallas de cliente las tratan como visitante y deben dejar ingresar.
 */
export const isClientSession = (session: Session | null): boolean => session?.user.role === 'CLIENT';

export function useSession(store: SessionStore = sessionStore): Session | null {
  return useSyncExternalStore(store.subscribe, store.get, () => null);
}
